import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { ListTodo } from 'lucide-react';
import { SidebarMenu, SidebarMenuBadge, SidebarMenuButton, SidebarMenuItem, useSidebar } from '@/components/ui/sidebar';
import { usePermission } from '@/hooks/usePermission';
import { usePref } from '@/hooks/use-pref';
import { UI_PREFS } from '@/lib/ui-prefs';
import { useTaskStore } from '@/stores/taskStore';
import { openTasksFor, tasksPanelHiddenOn, usePageTasks } from './usePageTasks';

/** Pinned to the foot of the sidebar: opens the Tasks panel for the page you're on. */
export function SidebarTasksButton() {
  const canView = usePermission('PROJECT_VIEW');
  const { pathname } = useLocation();
  const hidden = !canView || tasksPanelHiddenOn(pathname);
  const { board } = usePageTasks(!hidden);
  const panelOpen = useTaskStore((s) => s.panel.open);
  const setPanel = useTaskStore((s) => s.setPanel);
  const { isMobile, setOpenMobile } = useSidebar();
  const [, setChatOpen] = usePref(UI_PREFS.chatPanelOpen);

  const count = useMemo(() => {
    if (!board) return 0;
    const done = new Set(board.columns.filter((c) => c.is_done).map((c) => c.id));
    return openTasksFor(board.tasks, done).length;
  }, [board]);

  if (hidden) return null;
  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <SidebarMenuButton
          isActive={panelOpen}
          tooltip={count ? `Tasks on this page (${count})` : 'Tasks on this page'}
          aria-label={`Tasks on this page${count ? `: ${count} open` : ''}`}
          onClick={() => {
            setPanel({ open: !panelOpen, taskId: null });
            // It shares the right edge with the chat panel (T-78): opening one closes the other.
            if (!panelOpen) setChatOpen(false);
            if (isMobile) setOpenMobile(false);
          }}
        >
          <ListTodo />
          <span>Tasks on this page</span>
        </SidebarMenuButton>
        {count ? (
          <SidebarMenuBadge className="bg-primary text-2xs font-semibold text-primary-foreground peer-hover/menu-button:text-primary-foreground peer-data-[active=true]/menu-button:text-primary-foreground">
            {count}
          </SidebarMenuBadge>
        ) : null}
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
